import { useMemo } from 'react';
import { InvoiceRecord } from '@types';
import { useStatuses } from './useStatuses';

interface UseInvoiceStatusProps {
  currentStatus: string;
  formData: Partial<InvoiceRecord>;
}

export const useInvoiceStatus = ({
  currentStatus,
  formData
}: UseInvoiceStatusProps) => {
  const { getNextStatus, getStatusByKey, isLoading: isStatusLoading } = useStatuses();

  // Memoized calculation of next status based on classification and current status
  const getClassificationBasedNextStatus = useMemo(() => {
    // Don't calculate next status if status data is still loading
    if (isStatusLoading || !getStatusByKey) {
      return null;
    }
    
    const classification = formData?.classification || 'deliver_direct';
    
    // From Status 1 (Unprocessed) - branch based on classification
    if (currentStatus === 'unprocessed') {
      switch (classification) {
        case 'deliver_direct':
          return getStatusByKey('adding');
        case 'head_office':
          return getStatusByKey('head_office_stock');
        case 'expenses':
          return getStatusByKey('expense_invoices');
        case 'expense_invoice':
          return getStatusByKey('entering_expense');
        case 'credit_note':
          return getStatusByKey('credit_note');
        default:
          return getStatusByKey('adding'); // fallback to deliver_direct flow
      }
    }
    
    // From Status 2 (Adding) - branch based on is_new_stock and is_product_added checkboxes
    if (currentStatus === 'adding') {
      // If both is_new_stock AND is_product_added are true, skip to step 4 (IR)
      if (formData?.is_new_stock && formData?.is_product_added) {
        return getStatusByKey('ir');
      }
      // Otherwise, use original logic
      return formData?.is_new_stock ? getStatusByKey('new_products') : getStatusByKey('ir');
    }
    
    // From Status 3 (New Products) - return to Adding
    if (currentStatus === 'new_products') {
      return getStatusByKey('adding');
    }
    
    // From Status 10 (Credit Note) - move to credit entering
    if (currentStatus === 'credit_note') {
      return getStatusByKey('credit_entering');
    }
    
    // From Status 11 (Credit Entering) - move to completed
    if (currentStatus === 'credit_entering') {
      return getStatusByKey('completed');
    }
    
    // From Status 7 (Expense Invoices) - move to entering expense
    if (currentStatus === 'expense_invoices') {
      return getStatusByKey('completed');
    }
    
    // From Status 8 (Expense Entering) - move to completed
    if (currentStatus === 'entering_expense') {
      return getStatusByKey('completed');
    }
    
    // From Status 6 (HO Stock Invoices) - move to entering stock
    if (currentStatus === 'head_office_stock') {
      return getStatusByKey('entering_stock');
    }
    
    // From Status 8 (Entering Stock) - move to completed
    if (currentStatus === 'entering_stock') {
      return getStatusByKey('completed');
    }
    
    // From Status 5 (Invoicing) - move to entering stock
    if (currentStatus === 'invoicing') {
      return getStatusByKey('entering_stock');
    }
    
    // For other statuses, use linear progression
    return getNextStatus && getNextStatus(currentStatus);
  }, [currentStatus, formData?.classification, formData?.is_new_stock, formData?.is_product_added, isStatusLoading, getStatusByKey, getNextStatus]);
  
  // Memoized descriptive button text based on classification and current status
  const getNextStepButtonText = useMemo(() => {
    const classification = formData?.classification || 'deliver_direct';
    const nextStatus = getClassificationBasedNextStatus;
    
    if (currentStatus === 'unprocessed') {
      switch (classification) {
        case 'deliver_direct':
          return 'Move to Step 2: Adding';
        case 'head_office':
          return 'Move to Step 6: HO Stock Invoices';
        case 'expenses':
          return 'Move to Step 7: Expense Invoices';
        case 'expense_invoice':
          return 'Move to Step 9: Expense Entering';
        case 'credit_note':
          return 'Move to Step 10: Credit Note';
        default:
          return 'Move to Step 2: Adding';
      }
    }
    
    // Status 2 (Adding) - dynamic based on is_new_stock and is_product_added checkboxes
    if (currentStatus === 'adding') {
      // If both is_new_stock AND is_product_added are true, skip to step 4
      if (formData?.is_new_stock && formData?.is_product_added) {
        return 'Move to Step 4: IR';
      }
      // Otherwise, use original logic
      return formData?.is_new_stock 
        ? 'Move to Step 3: New Products' 
        : 'Move to Step 4: IR';
    }
    
    // Status 3 (New Products) - return to Adding
    if (currentStatus === 'new_products') {
      return 'Return to Step 2: Adding';
    }
    
    // Status 4 (IR) - move to invoicing
    if (currentStatus === 'ir') {
      return 'Move to Step 5: Invoicing';
    }
    
    // Status 5 (Invoicing) - move to entering stock
    if (currentStatus === 'invoicing') {
      return 'Move to Step 8: Dealer Direct & Head Office Stock Entering';
    }
    
    // Status 10 (Credit Note) - move to credit entering
    if (currentStatus === 'credit_note') {
      return 'Move to Step 11: Credit Note Entering';
    }
    
    // Status 11 (Credit Entering) - move to completed
    if (currentStatus === 'credit_entering') {
      return 'Move to Step 12: Completed';
    }
    
    // Status 7 (Expense Invoices) - move to entering expense
    if (currentStatus === 'expense_invoices') {
      return 'Move to Step 12: Completed';
    }
    
    // Status 9 (Expense Entering) - move to completed
    if (currentStatus === 'entering_expense') {
      return 'Move to Step 12: Completed';
    }
    
    // Status 6 (HO Stock Invoices) - move to entering stock
    if (currentStatus === 'head_office_stock') {
      return 'Move to Step 8: Dealer Direct & Head Office Stock Entering';
    }
    
    // Status 8 (Entering Stock) - move to completed
    if (currentStatus === 'entering_stock') {
      return 'Move to Step 12: Completed';
    }
    
    // For other statuses, use the status label
    return nextStatus ? `Next Step (${nextStatus.label})` : 'Next Step';
  }, [currentStatus, formData?.classification, formData?.is_new_stock, formData?.is_product_added, getClassificationBasedNextStatus]);

  // Memoized formatted status display name
  const getStatusDisplayName = useMemo(() => {
    if (isStatusLoading || !getStatusByKey) {
      return currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).replace(/_/g, ' ');
    }
    const status = getStatusByKey(currentStatus);
    return status?.label || currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1).replace(/_/g, ' ');
  }, [isStatusLoading, getStatusByKey, currentStatus]);

  return {
    nextStatus: getClassificationBasedNextStatus,
    nextStepButtonText: getNextStepButtonText,
    statusDisplayName: getStatusDisplayName,
    isStatusLoading
  };
};
